import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { FaCamera, FaChevronLeft, FaVideo } from "react-icons/fa";
import { markSectionVisited } from "../../utils/providerOnboarding";
import {
  ALLOWED_PHOTO_TYPES,
  GENDER_OPTIONS,
  LANGUAGE_OPTIONS,
  MAX_PHOTO_BYTES,
  MAX_TOP_SPECIALTIES,
  POPULATION_OPTIONS,
  RACE_OPTIONS,
  SPECIALTY_OPTIONS,
  buildProfileFormState,
  formatPhoneInput,
  mergeOptions,
  serializeSpecialties,
  validateProfileForm,
} from "../../utils/providerProfileFields";
import classes from "./ProfileInformationOnboarding.module.css";

const HollowStar = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 3.5l2.6 5.3 5.9.9-4.3 4.2 1 5.8L12 16.9 6.8 19.7l1-5.8L3.5 9.7l5.9-.9L12 3.5z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

const FilledStar = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.2 1 5.8L12 16.9 6.8 19.7l1-5.8L3.5 9.7l5.9-.9L12 3.5z" />
  </svg>
);

const stringifyComparable = (form) =>
  JSON.stringify({
    ...form,
    profilePhotoFile: form.profilePhotoFile ? "pending" : null,
    videoIntroFile: form.videoIntroFile ? "pending" : null,
    removeVideo: Boolean(form.removeVideo),
  });

const ProfileInformationOnboarding = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state?.user?.user);
  const providerId = user?.providerId;
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const [provider, setProvider] = useState(null);
  const [form, setForm] = useState(null);
  const [baseline, setBaseline] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [pendingLeavePath, setPendingLeavePath] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [stickyStyle, setStickyStyle] = useState({
    left: 0,
    width: "100%",
  });

  const isDirty = useMemo(() => {
    if (!form || !baseline) return false;
    return stringifyComparable(form) !== baseline;
  }, [form, baseline]);

  const updateStickyPosition = useCallback(() => {
    const main = document.querySelector(".app-main");
    if (!main) {
      setStickyStyle({ left: 0, width: "100%" });
      return;
    }
    const rect = main.getBoundingClientRect();
    setStickyStyle({
      left: Math.max(0, rect.left),
      width: Math.max(0, rect.width),
    });
  }, []);

  useEffect(() => {
    updateStickyPosition();
    window.addEventListener("resize", updateStickyPosition);
    window.addEventListener("scroll", updateStickyPosition, true);

    const main = document.querySelector(".app-main");
    let observer;
    if (main && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(updateStickyPosition);
      observer.observe(main);
    }

    return () => {
      window.removeEventListener("resize", updateStickyPosition);
      window.removeEventListener("scroll", updateStickyPosition, true);
      if (observer) observer.disconnect();
    };
  }, [updateStickyPosition]);

  useEffect(() => {
    if (isDirty) {
      updateStickyPosition();
    }
  }, [isDirty, updateStickyPosition]);

  const loadProvider = useCallback(async () => {
    if (!providerId) {
      setLoading(false);
      setLoadError("Provider profile not found.");
      return;
    }

    setLoading(true);
    setLoadError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/specialist/provider/${providerId}`,
        user?.token
          ? { headers: { Authorization: `Bearer ${user.token}` } }
          : undefined,
      );

      if (!response.data?.success) {
        setLoadError(response.data?.message || "Failed to load profile.");
        return;
      }

      const data = response.data.data;
      const nextForm = {
        ...buildProfileFormState(data),
        profilePhotoFile: null,
        videoIntroFile: null,
        removeVideo: false,
      };
      setProvider(data);
      setForm(nextForm);
      setBaseline(stringifyComparable(nextForm));
      setPhotoPreview(data.profilePhotoUrl || null);
      setErrors({});
      markSectionVisited("profile", providerId);
    } catch (err) {
      setLoadError(
        err.response?.data?.message || "Failed to load profile information.",
      );
    } finally {
      setLoading(false);
    }
  }, [providerId, user?.token]);

  useEffect(() => {
    loadProvider();
  }, [loadProvider]);

  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    return () => {
      if (photoPreview && photoPreview.startsWith("blob:")) {
        URL.revokeObjectURL(photoPreview);
      }
    };
  }, [photoPreview]);

  useEffect(() => {
    return () => {
      if (videoPreview && videoPreview.startsWith("blob:")) {
        URL.revokeObjectURL(videoPreview);
      }
    };
  }, [videoPreview]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const toggleListValue = (field, option) => {
    setForm((prev) => {
      const current = prev[field] || [];
      const next = current.includes(option)
        ? current.filter((item) => item !== option)
        : [...current, option];
      return { ...prev, [field]: next };
    });
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const toggleSpecialty = (option) => {
    setForm((prev) => {
      const selected = prev.specialties || [];
      const starred = prev.starredSpecialties || [];
      if (selected.includes(option)) {
        return {
          ...prev,
          specialties: selected.filter((item) => item !== option),
          starredSpecialties: starred.filter((item) => item !== option),
        };
      }
      return { ...prev, specialties: [...selected, option] };
    });
    setErrors((prev) => {
      if (!prev.specialties) return prev;
      const next = { ...prev };
      delete next.specialties;
      return next;
    });
  };

  const toggleSpecialtyStar = (option) => {
    setForm((prev) => {
      const selected = prev.specialties || [];
      if (!selected.includes(option)) return prev;
      const starred = prev.starredSpecialties || [];
      if (starred.includes(option)) {
        return {
          ...prev,
          starredSpecialties: starred.filter((item) => item !== option),
        };
      }
      if (starred.length >= MAX_TOP_SPECIALTIES) {
        toast.warning(
          `Only ${MAX_TOP_SPECIALTIES} top specialties allowed. Unstar one first.`,
        );
        return prev;
      }
      return { ...prev, starredSpecialties: [...starred, option] };
    });
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
      toast.error("Please upload a JPG, PNG, or GIF image.");
      event.target.value = "";
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error("Profile photo must be 5MB or smaller.");
      event.target.value = "";
      return;
    }

    if (photoPreview && photoPreview.startsWith("blob:")) {
      URL.revokeObjectURL(photoPreview);
    }
    const previewUrl = URL.createObjectURL(file);
    setPhotoPreview(previewUrl);
    updateField("profilePhotoFile", file);
    setErrors((prev) => {
      if (!prev.profilePhoto) return prev;
      const next = { ...prev };
      delete next.profilePhoto;
      return next;
    });
  };

  const handleVideoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Please upload a valid video file.");
      event.target.value = "";
      return;
    }
    if (videoPreview && videoPreview.startsWith("blob:")) {
      URL.revokeObjectURL(videoPreview);
    }
    setVideoPreview(URL.createObjectURL(file));
    updateField("videoIntroFile", file);
    updateField("removeVideo", false);
  };

  const requestLeave = (path = "/dashboard") => {
    if (isDirty) {
      setPendingLeavePath(path);
      setShowDiscardModal(true);
      return;
    }
    navigate(path);
  };

  const discardChanges = () => {
    setShowDiscardModal(false);
    const path = pendingLeavePath || "/dashboard";
    setPendingLeavePath(null);
    if (path === "__reset__") {
      if (!provider) return;
      const nextForm = {
        ...buildProfileFormState(provider),
        profilePhotoFile: null,
        videoIntroFile: null,
        removeVideo: false,
      };
      setForm(nextForm);
      setBaseline(stringifyComparable(nextForm));
      setPhotoPreview(provider.profilePhotoUrl || null);
      setVideoPreview(null);
      setErrors({});
      if (photoInputRef.current) photoInputRef.current.value = "";
      if (videoInputRef.current) videoInputRef.current.value = "";
      return;
    }
    navigate(path);
  };

  const keepEditing = () => {
    setShowDiscardModal(false);
    setPendingLeavePath(null);
  };

  const handleCancel = () => {
    if (!isDirty) return;
    setPendingLeavePath("__reset__");
    setShowDiscardModal(true);
  };

  const handleSave = async () => {
    if (!form || !providerId) return;

    const nextErrors = validateProfileForm(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      toast.error("Please fix the highlighted required fields.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        email: form.email.trim(),
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        displayName: form.displayName.trim(),
        yearsOfExperience: Number(form.yearsOfExperience),
        gender: form.genderIdentity,
        genderIdentity: form.genderIdentity,
        race: form.raceEthnicity,
        raceEthnicity: form.raceEthnicity,
        languages: form.languages.join(", "),
        populationsServed: form.populationsServed.join(", "),
        phone: form.phone.trim(),
        phoneNumber: form.phone.trim(),
        phoneno: form.phone.trim(),
        bio: form.bio.trim(),
        shortBio: form.bio.trim(),
        specialisation: serializeSpecialties(
          form.specialties,
          form.starredSpecialties,
        ),
        providerType: provider?.providerType || "Therapist",
        time_zone: provider?.time_zone,
        ...(Array.isArray(provider?.credentials)
          ? { credentials: provider.credentials }
          : {}),
        ...(Array.isArray(provider?.insurance)
          ? { insurance: provider.insurance }
          : {}),
        fees: provider?.fees,
      };

      let response;
      const needsMultipart = Boolean(
        form.profilePhotoFile || form.videoIntroFile,
      );

      if (needsMultipart) {
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (value === null || value === undefined) return;
          if (typeof value === "object") {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        });
        if (form.profilePhotoFile) {
          formData.append("profilePhoto", form.profilePhotoFile);
        }
        if (form.videoIntroFile) {
          formData.append("videoIntro", form.videoIntroFile);
        }
        if (form.removeVideo) {
          formData.append("videoIntroUrl", "");
        }

        response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/specialist/editprovider/${providerId}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
      } else {
        response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/specialist/editprovider/${providerId}`,
          {
            ...payload,
            ...(form.removeVideo ? { videoIntroUrl: "", videoIntro: null } : {}),
          },
        );
      }

      if (!response.data?.success) {
        throw new Error(response.data?.message || "Failed to save profile.");
      }

      const saved = response.data.data || {};
      const merged = { ...provider, ...saved, ...payload };
      if (saved.profilePhotoUrl) merged.profilePhotoUrl = saved.profilePhotoUrl;
      if (form.removeVideo) merged.videoIntroUrl = "";
      if (saved.videoIntroUrl) merged.videoIntroUrl = saved.videoIntroUrl;

      const nextForm = {
        ...buildProfileFormState(merged),
        profilePhotoFile: null,
        videoIntroFile: null,
        removeVideo: false,
      };
      setProvider(merged);
      setForm(nextForm);
      setBaseline(stringifyComparable(nextForm));
      setPhotoPreview(merged.profilePhotoUrl || null);
      setVideoPreview(null);
      setErrors({});
      if (photoInputRef.current) photoInputRef.current.value = "";
      if (videoInputRef.current) videoInputRef.current.value = "";

      toast.success("Profile information saved successfully.");
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Failed to save profile information.",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className={classes.loading}>Loading profile information...</div>;
  }

  if (loadError || !form) {
    return <div className={classes.error}>{loadError || "Unable to load profile."}</div>;
  }

  const languageOptions = mergeOptions(LANGUAGE_OPTIONS, form.languages);
  const populationOptions = mergeOptions(
    POPULATION_OPTIONS,
    form.populationsServed,
  );
  const specialtyOptions = mergeOptions(SPECIALTY_OPTIONS, form.specialties);
  const atStarLimit = (form.starredSpecialties || []).length >= MAX_TOP_SPECIALTIES;
  const hasVideo =
    Boolean(form.videoIntroFile) ||
    (Boolean(form.videoIntroUrl) && !form.removeVideo);

  return (
    <div className={classes.page}>
      <div className={classes.header}>
        <button
          type="button"
          className={classes.backBtn}
          aria-label="Back to onboarding"
          onClick={() => requestLeave("/dashboard")}
        >
          <FaChevronLeft />
        </button>
        <h1 className={classes.title}>Profile Information</h1>
      </div>

      {/* Profile Photo */}
      <section className={classes.card}>
        <h2 className={classes.cardTitle}>
          Profile Photo <span className={classes.required}>*</span>
        </h2>
        <p className={classes.cardSubtitle}>
          Basic identity and professional role details
        </p>
        <div className={classes.photoRow}>
          <div className={classes.avatar}>
            {photoPreview ? (
              <img src={photoPreview} alt="Profile" />
            ) : (
              <FaCamera size={28} />
            )}
          </div>
          <div className={classes.photoActions}>
            <button
              type="button"
              className={classes.uploadBtn}
              onClick={() => photoInputRef.current?.click()}
            >
              Upload Photo
            </button>
            <p className={classes.helpText}>
              .JPG, .PNG, or GIF. Max size of 5MB.
            </p>
            {errors.profilePhoto && (
              <p className={classes.errorText}>{errors.profilePhoto}</p>
            )}
          </div>
          <input
            ref={photoInputRef}
            className={classes.hiddenInput}
            type="file"
            accept="image/jpeg,image/png,image/gif"
            onChange={handlePhotoChange}
          />
        </div>
      </section>

      {/* Personal Info */}
      <section className={classes.card}>
        <h2 className={classes.cardTitle}>Personal Info</h2>
        <div className={classes.grid2} style={{ marginTop: 16 }}>
          <div className={classes.field}>
            <label className={classes.label}>
              First Name <span className={classes.required}>*</span>
            </label>
            <input
              className={`${classes.input} ${
                errors.firstname ? classes.inputError : ""
              }`}
              value={form.firstname}
              onChange={(e) => updateField("firstname", e.target.value)}
            />
            {errors.firstname && (
              <p className={classes.errorText}>{errors.firstname}</p>
            )}
          </div>
          <div className={classes.field}>
            <label className={classes.label}>
              Last Name <span className={classes.required}>*</span>
            </label>
            <input
              className={`${classes.input} ${
                errors.lastname ? classes.inputError : ""
              }`}
              value={form.lastname}
              onChange={(e) => updateField("lastname", e.target.value)}
            />
            {errors.lastname && (
              <p className={classes.errorText}>{errors.lastname}</p>
            )}
          </div>
          <div className={classes.field}>
            <label className={classes.label}>
              Display Name <span className={classes.required}>*</span>
            </label>
            <input
              className={`${classes.input} ${
                errors.displayName ? classes.inputError : ""
              }`}
              value={form.displayName}
              onChange={(e) => updateField("displayName", e.target.value)}
            />
            {errors.displayName && (
              <p className={classes.errorText}>{errors.displayName}</p>
            )}
          </div>
          <div className={classes.field}>
            <label className={classes.label}>
              Years of Experience <span className={classes.required}>*</span>
            </label>
            <input
              type="number"
              min="0"
              className={`${classes.input} ${
                errors.yearsOfExperience ? classes.inputError : ""
              }`}
              value={form.yearsOfExperience}
              onChange={(e) => updateField("yearsOfExperience", e.target.value)}
            />
            {errors.yearsOfExperience && (
              <p className={classes.errorText}>{errors.yearsOfExperience}</p>
            )}
          </div>
        </div>
      </section>

      {/* Background & Identity */}
      <section className={classes.card}>
        <h2 className={classes.cardTitle}>Background &amp; Identity</h2>
        <div className={classes.grid2} style={{ marginTop: 16 }}>
          <div className={classes.field}>
            <label className={classes.label}>
              Gender Identity <span className={classes.required}>*</span>
            </label>
            <select
              className={`${classes.select} ${
                errors.genderIdentity ? classes.inputError : ""
              }`}
              value={form.genderIdentity}
              onChange={(e) => updateField("genderIdentity", e.target.value)}
            >
              <option value="">Select</option>
              {GENDER_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {errors.genderIdentity && (
              <p className={classes.errorText}>{errors.genderIdentity}</p>
            )}
          </div>
          <div className={classes.field}>
            <label className={classes.label}>
              Race &amp; Ethnicity <span className={classes.required}>*</span>
            </label>
            <select
              className={`${classes.select} ${
                errors.raceEthnicity ? classes.inputError : ""
              }`}
              value={form.raceEthnicity}
              onChange={(e) => updateField("raceEthnicity", e.target.value)}
            >
              <option value="">Select</option>
              {RACE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {errors.raceEthnicity && (
              <p className={classes.errorText}>{errors.raceEthnicity}</p>
            )}
          </div>
          <div className={`${classes.field} ${classes.fieldFull}`}>
            <label className={classes.label}>
              Languages Spoken <span className={classes.required}>*</span>
            </label>
            <div className={classes.chipGrid}>
              {languageOptions.map((option) => {
                const selected = form.languages.includes(option);
                return (
                  <button
                    key={option}
                    type="button"
                    className={`${classes.chip} ${
                      selected ? classes.chipSelected : ""
                    }`}
                    onClick={() => toggleListValue("languages", option)}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
            {errors.languages && (
              <p className={classes.errorText}>{errors.languages}</p>
            )}
          </div>
          <div className={`${classes.field} ${classes.fieldFull}`}>
            <label className={classes.label}>
              Populations Served <span className={classes.required}>*</span>
            </label>
            <div className={classes.chipGrid}>
              {populationOptions.map((option) => {
                const selected = form.populationsServed.includes(option);
                return (
                  <button
                    key={option}
                    type="button"
                    className={`${classes.chip} ${
                      selected ? classes.chipSelected : ""
                    }`}
                    onClick={() => toggleListValue("populationsServed", option)}
                  >
                    {option}
                  </button>
                );
              })}
            </div>
            {errors.populationsServed && (
              <p className={classes.errorText}>{errors.populationsServed}</p>
            )}
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className={classes.card}>
        <h2 className={classes.cardTitle}>Contact Info</h2>
        <div className={classes.grid2} style={{ marginTop: 16 }}>
          <div className={classes.field}>
            <label className={classes.label}>
              Email <span className={classes.required}>*</span>
            </label>
            <input
              type="email"
              className={`${classes.input} ${
                errors.email ? classes.inputError : ""
              }`}
              value={form.email}
              onChange={(e) => updateField("email", e.target.value)}
            />
            {errors.email && (
              <p className={classes.errorText}>{errors.email}</p>
            )}
          </div>
          <div className={classes.field}>
            <label className={classes.label}>
              Phone <span className={classes.required}>*</span>
            </label>
            <input
              className={`${classes.input} ${
                errors.phone ? classes.inputError : ""
              }`}
              value={form.phone}
              onChange={(e) =>
                updateField("phone", formatPhoneInput(e.target.value))
              }
              placeholder="(415) 555-0142"
            />
            {errors.phone && (
              <p className={classes.errorText}>{errors.phone}</p>
            )}
          </div>
        </div>
      </section>

      {/* Short Bio */}
      <section className={classes.card}>
        <h2 className={classes.cardTitle}>
          Short Bio <span className={classes.required}>*</span>
        </h2>
        <p className={classes.cardSubtitle}>
          A brief professional statement for the patient directory
        </p>
        <textarea
          className={`${classes.textarea} ${
            errors.bio ? classes.inputError : ""
          }`}
          value={form.bio}
          onChange={(e) => updateField("bio", e.target.value)}
          placeholder="Write a short bio patients will see..."
        />
        {errors.bio && <p className={classes.errorText}>{errors.bio}</p>}
      </section>

      {/* Specialties */}
      <section className={classes.card}>
        <h2 className={classes.cardTitle}>
          Specialties <span className={classes.required}>*</span>
        </h2>
        <p className={classes.cardSubtitle}>
          Highlight your areas of clinical focus (Star your top 3 specialties).
        </p>
        <div className={classes.chipGrid}>
          {specialtyOptions.map((option) => {
            const selected = form.specialties.includes(option);
            const starred = form.starredSpecialties.includes(option);
            return (
              <button
                key={option}
                type="button"
                className={`${classes.chip} ${
                  selected ? classes.chipSelected : ""
                }`}
                onClick={() => toggleSpecialty(option)}
              >
                {selected && (
                  <span
                    className={`${classes.starBtn} ${
                      starred ? classes.starFilled : ""
                    }`}
                    role="button"
                    tabIndex={0}
                    aria-label={
                      starred
                        ? `Unstar ${option}`
                        : `Star ${option} as top specialty`
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSpecialtyStar(option);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleSpecialtyStar(option);
                      }
                    }}
                    title={
                      !starred && atStarLimit
                        ? `Only ${MAX_TOP_SPECIALTIES} top specialties allowed`
                        : undefined
                    }
                  >
                    {starred ? <FilledStar /> : <HollowStar />}
                  </span>
                )}
                <span>{option}</span>
              </button>
            );
          })}
        </div>
        {errors.specialties && (
          <p className={classes.errorText}>{errors.specialties}</p>
        )}
      </section>

      {/* Intro Video */}
      <section className={classes.card}>
        <h2 className={classes.cardTitle}>Intro Video</h2>
        <p className={classes.cardSubtitle}>
          A brief intro video for patients to see.
        </p>
        {!hasVideo ? (
          <div className={classes.videoBox}>
            <FaVideo size={28} color="#9ca3af" />
            <p className={classes.videoEmptyText}>No intro video added yet.</p>
            <button
              type="button"
              className={classes.outlineBtn}
              onClick={() => videoInputRef.current?.click()}
            >
              Add Video
            </button>
          </div>
        ) : (
          <>
            <div className={`${classes.videoBox} ${classes.videoBoxHasMedia}`}>
              <video
                className={classes.videoPlayer}
                controls
                src={videoPreview || form.videoIntroUrl}
              />
            </div>
            <div className={classes.videoActions}>
              <button
                type="button"
                className={classes.outlineBtn}
                onClick={() => videoInputRef.current?.click()}
              >
                Replace Video
              </button>
              <button
                type="button"
                className={classes.dangerBtn}
                onClick={() => {
                  if (videoPreview && videoPreview.startsWith("blob:")) {
                    URL.revokeObjectURL(videoPreview);
                  }
                  setVideoPreview(null);
                  updateField("videoIntroFile", null);
                  updateField("removeVideo", true);
                  if (videoInputRef.current) videoInputRef.current.value = "";
                }}
              >
                Remove
              </button>
            </div>
          </>
        )}
        <input
          ref={videoInputRef}
          className={classes.hiddenInput}
          type="file"
          accept="video/*"
          onChange={handleVideoChange}
        />
      </section>

      {isDirty &&
        createPortal(
          <div
            className={classes.stickyBar}
            style={{
              left: stickyStyle.left,
              width: stickyStyle.width,
            }}
            role="region"
            aria-label="Unsaved profile changes"
          >
            <div className={classes.stickyInner}>
              <span className={classes.unsavedLabel}>Unsaved changes</span>
              <div className={classes.stickyActions}>
                <button
                  type="button"
                  className={classes.cancelBtn}
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={classes.saveBtn}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {showDiscardModal &&
        createPortal(
          <div className={classes.modalOverlay} role="presentation">
            <div className={classes.modal} role="dialog" aria-modal="true">
              <h3 className={classes.modalTitle}>Discard unsaved changes?</h3>
              <p className={classes.modalText}>
                You have unsaved edits on this page. If you leave now, those
                changes will be lost.
              </p>
              <div className={classes.modalActions}>
                <button
                  type="button"
                  className={classes.keepBtn}
                  onClick={keepEditing}
                >
                  Keep Editing
                </button>
                <button
                  type="button"
                  className={classes.discardBtn}
                  onClick={discardChanges}
                >
                  Discard Changes
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ProfileInformationOnboarding;
